import React, { FunctionComponent } from 'react';

/**
 * The horizontal logo's shapes (viewBox 0 0 153 36), shared with the canvases that sign with it (the
 * «Tu mes» stories).
 */
export const AGORA_PATHS = {
  light:
    'M41.3822 30.3938L38.187 35.7712L3.11719 35.8491L17.5348 10.9106L20.6466 16.2974L15.7314 24.9347L12.625 30.3938H41.3822Z',
  blue: 'M20.6467 16.2973L17.5349 10.9106L3.11731 35.8491L0 30.3938L17.5349 0L31.8745 24.9385H25.6399L20.6467 16.2973Z',
  red: 'M23.8473 0L41.3822 30.3938H12.625L15.7314 24.9346L15.7384 24.9385H25.6398H31.8744L17.5348 0H23.8473Z',
  word: 'M76.7893 7.41158V4.76367H69.5647V7.41158H76.7893ZM72.2126 26.6334C74.4029 26.6334 76.3316 25.7835 77.6065 24.2797V26.3065H81.2351V10.1249H77.6065V12.1844C76.3316 10.6806 74.4029 9.79797 72.2126 9.79797C67.7668 9.79797 64.7266 13.5573 64.7266 18.232C64.7266 22.8741 67.7668 26.6334 72.2126 26.6334ZM73.1279 23.1356C70.382 23.1356 68.4206 21.0761 68.4206 18.232C68.4206 15.388 70.382 13.2958 73.1279 13.2958C75.7105 13.2958 77.6065 15.388 77.6065 18.232C77.6065 21.1088 75.6778 23.1356 73.1279 23.1356ZM92.2844 31.112C96.9264 31.112 100.718 28.2679 100.718 22.6125V10.1249H97.2533V12.5439C96.4034 10.9421 94.2131 9.79797 91.7614 9.79797C87.2828 9.79797 84.3407 13.2958 84.3407 17.6436C84.3407 21.9914 87.2828 25.4566 91.7614 25.4566C94.7035 25.4566 96.4361 23.9855 97.0899 22.6779V22.8087C97.0899 26.1104 95.39 28.3333 91.9248 28.3333C90.2576 28.3333 88.3616 27.7776 87.0867 26.6661L85.681 28.9544C87.1847 30.2947 89.5058 31.112 92.2844 31.112ZM92.5459 22.2856C89.9961 22.2856 88.0347 20.3242 88.0347 17.6436C88.0347 14.963 89.9961 12.9689 92.5459 12.9689C95.0958 12.9689 97.0899 14.9303 97.0899 17.5129C97.0899 20.3242 95.1612 22.2856 92.5459 22.2856ZM112.422 26.6334C117.194 26.6334 121.019 22.9067 121.019 18.232C121.019 13.5247 117.194 9.79797 112.422 9.79797C107.649 9.79797 103.824 13.5247 103.824 18.232C103.824 22.9067 107.649 26.6334 112.422 26.6334ZM112.422 23.1356C109.643 23.1356 107.518 21.0434 107.518 18.232C107.518 15.388 109.643 13.2958 112.422 13.2958C115.2 13.2958 117.325 15.388 117.325 18.232C117.325 21.0434 115.2 23.1356 112.422 23.1356ZM127.753 26.3065V19.7031C127.753 15.4534 130.042 13.7535 133.605 13.7535V9.79797C130.14 9.79797 128.374 11.694 127.753 13.3285V10.1249H124.125V26.3065H127.753ZM143.118 26.6334C145.308 26.6334 147.237 25.7835 148.512 24.2797V26.3065H152.14V10.1249H148.512V12.1844C147.237 10.6806 145.308 9.79797 143.118 9.79797C138.672 9.79797 135.632 13.5573 135.632 18.232C135.632 22.8741 138.672 26.6334 143.118 26.6334ZM144.033 23.1356C141.287 23.1356 139.326 21.0761 139.326 18.232C139.326 15.388 141.287 13.2958 144.033 13.2958C146.616 13.2958 148.512 15.388 148.512 18.232C148.512 21.1088 146.583 23.1356 144.033 23.1356Z'
};

type Type = 'horizontal' | 'vertical' | 'triangle' | 'plain';

type LogoProps = {
  type: Type;
  width?: string;
  height?: string;
  color?: string;
};

export const Logo: FunctionComponent<LogoProps> = ({
  type,
  width = '100%',
  height = '100%',
  color = 'currentColor'
}) => (
  <svg
    width={width}
    height={height}
    viewBox={viewBox[type]}
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
  >
    {getPath(type, color as string)}
  </svg>
);

const viewBox: Record<Type, string> = {
  horizontal: '0 0 153 36',
  vertical: '0 0 236.2 205.2',
  triangle: '0 0 115.3 98.8',
  plain: '0 0 180 54'
};

const getPath = (type: Type, color: string) => {
  switch (type) {
    case 'horizontal':
      return (
        <>
          <path d={AGORA_PATHS.light} fill="#E2E8F0" />
          <path
            d="M17.5351 10.9106L20.6469 16.2974L16.7342 23.0921L12.1689 20.1848L17.5351 10.9106Z"
            fill="url(#paint0_linear_17360_17535)"
            fillOpacity="0.7"
          />
          <path d={AGORA_PATHS.blue} fill="#0072FF" />
          <path
            d="M31.8543 24.9244L25.6302 24.9316L21.7061 18.1387L26.5114 15.6305L31.8543 24.9244Z"
            fill="url(#paint1_linear_17360_17535)"
            fillOpacity="0.7"
          />
          <path d={AGORA_PATHS.red} fill="#FF4658" />
          <path
            d="M12.625 30.3937L15.7289 24.9387L23.7202 24.9194L23.4706 30.3746L12.625 30.3937Z"
            fill="url(#paint2_linear_17360_17535)"
            fillOpacity="0.7"
          />
          <path d={AGORA_PATHS.word} fill="currentColor" />
          <rect
            x="52.7876"
            y="3.5874"
            width="0.683334"
            height="28.5292"
            fill="currentColor"
            fillOpacity="0.3"
          />
          <defs>
            <linearGradient
              id="paint0_linear_17360_17535"
              x1="18.8455"
              y1="15.9076"
              x2="12.5252"
              y2="18.5424"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopOpacity="0.3" />
              <stop offset="0.252573" stopOpacity="0.137748" />
              <stop offset="0.490551" stopOpacity="0.01" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_17360_17535"
              x1="26.8652"
              y1="23.5719"
              x2="27.7524"
              y2="16.7522"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopOpacity="0.3" />
              <stop offset="0.252573" stopOpacity="0.137748" />
              <stop offset="0.490551" stopOpacity="0.01" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_17360_17535"
              x1="16.3401"
              y1="26.6997"
              x2="21.8393"
              y2="30.8946"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopOpacity="0.3" />
              <stop offset="0.252573" stopOpacity="0.137748" />
              <stop offset="0.490551" stopOpacity="0.01" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
          </defs>
        </>
      );
    case 'vertical':
      return (
        <>
          <polygon
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_36_"
            fill="#2459F5"
            points="111.9,0 151.6,68.9 134.3,68.9 111.9,30.6 71.8,98.8 62.9,83.2 "
          />
          <polygon
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_35_"
            fill="#E04E50"
            points="178.1,83.7 98.6,84.3 107.5,68.9 151.6,68.9 111.9,0 130,0 "
          />
          <polygon
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_33_"
            fill="#E7E3E8"
            points="71.8,98.8 111.9,30.6 120.8,45.9 98.6,84.3 178.1,83.7 171.7,98.8 "
          />
          <g xmlns="http://www.w3.org/2000/svg" id="XMLID_2_">
            <path
              id="XMLID_17_"
              d="M0,168.5c0-6.8,1.9-12.5,5.8-17.1c3.9-4.6,8.8-6.9,14.8-6.9c6,0,10.7,2.7,14.2,8.2v-7.3h9.8v46.1h-9.8v-7.2   c-3.5,5.4-8.2,8.1-14.2,8.1c-6,0-11-2.3-14.8-6.9C1.9,180.9,0,175.2,0,168.5z M13.6,158.5c-2.4,2.7-3.6,6-3.6,10.1   c0,4,1.2,7.4,3.6,10c2.4,2.6,5.4,4,9.2,4c3.5,0,6.4-1.3,8.7-3.9c2.3-2.6,3.4-6,3.4-10.1c0-4-1.1-7.4-3.4-10.1c-2.3-2.7-5.2-4-8.7-4   C19,154.5,16,155.8,13.6,158.5z"
            />
            <path
              id="XMLID_20_"
              d="M53.4,166.4c0-6.2,1.9-11.4,5.8-15.6c3.9-4.2,8.8-6.3,14.8-6.3c2.8,0,5.4,0.7,7.8,2c2.4,1.3,4.4,3.1,6,5.4   v-6.4h9.8v35.7c0,4.1-0.7,7.7-2.1,10.9c-1.4,3.2-3.3,5.7-5.5,7.5c-2.3,1.8-4.7,3.2-7.3,4.1c-2.6,0.9-5.2,1.4-7.9,1.4   c-7.3,0-13.3-2.1-17.9-6.3l4.3-6.9c3.5,3.4,7.9,5,13.1,5c0.9,0,1.8-0.1,2.9-0.3c1-0.2,2.2-0.7,3.5-1.3c1.3-0.7,2.4-1.5,3.4-2.4   c1-1,1.9-2.3,2.6-3.9c0.7-1.6,1.1-3.5,1.1-5.5v-1.9c-3.2,4.8-7.8,7.3-13.8,7.3c-6,0-11-2.2-14.8-6.5   C55.3,178.1,53.4,172.8,53.4,166.4z M63.4,166.7c0,3.8,1.1,6.8,3.3,9.2s5.1,3.5,8.5,3.5c3.5,0,6.3-1.2,8.5-3.5   c2.2-2.3,3.3-5.4,3.3-9.2c0-3.7-1.1-6.7-3.3-9.1c-2.2-2.4-5-3.6-8.4-3.6c-3.4,0-6.2,1.2-8.5,3.6C64.5,160.1,63.4,163.1,63.4,166.7z   "
            />
            <path
              id="XMLID_25_"
              d="M106.4,168.5c0-6.6,2.3-12.3,6.8-17c4.5-4.7,10-7,16.4-7c6.4,0,11.9,2.3,16.4,7c4.5,4.7,6.8,10.3,6.8,17   c0,6.6-2.3,12.2-6.8,16.9c-4.5,4.7-10,7-16.4,7c-6.4,0-11.9-2.3-16.4-7C108.7,180.7,106.4,175.1,106.4,168.5z M116.4,168.5   c0,3.9,1.3,7.2,3.9,9.9c2.6,2.7,5.7,4,9.4,4c3.7,0,6.8-1.3,9.4-4c2.6-2.7,3.9-6,3.9-9.9c0-3.8-1.3-7.2-3.9-9.9   c-2.6-2.8-5.7-4.1-9.4-4.1c-3.7,0-6.8,1.4-9.4,4.1C117.7,161.4,116.4,164.7,116.4,168.5z"
            />
            <path
              id="XMLID_40_"
              d="M161.7,191.5v-46.1h9.8v9.1c1-2.7,2.8-5.1,5.4-7.1c2.6-2,6.1-3,10.4-3v11.3c-10.6,0-15.8,5.6-15.8,16.9   v18.8H161.7z"
            />
            <path
              id="XMLID_42_"
              d="M191.6,168.5c0-6.8,1.9-12.5,5.8-17.1c3.9-4.6,8.8-6.9,14.8-6.9c6,0,10.7,2.7,14.2,8.2v-7.3h9.8v46.1h-9.8   v-7.2c-3.5,5.4-8.2,8.1-14.2,8.1c-6,0-11-2.3-14.8-6.9C193.5,180.9,191.6,175.2,191.6,168.5z M205.1,158.5c-2.4,2.7-3.6,6-3.6,10.1   c0,4,1.2,7.4,3.6,10c2.4,2.6,5.4,4,9.2,4c3.5,0,6.4-1.3,8.7-3.9c2.3-2.6,3.4-6,3.4-10.1c0-4-1.1-7.4-3.4-10.1c-2.3-2.7-5.2-4-8.7-4   C210.6,154.5,207.5,155.8,205.1,158.5z"
            />
          </g>
          <rect
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_31_"
            x="12"
            y="129.8"
            width="20.5"
            height="8.5"
          />
          <linearGradient
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_127_"
            gradientUnits="userSpaceOnUse"
            x1="122.5452"
            y1="44.9659"
            x2="118.3786"
            y2="47.9659"
          >
            <stop offset="0" style={{ stopColor: '#001E2A' }} />
            <stop offset="1" style={{ stopColor: '#E7E3E8', stopOpacity: 0 }} />
          </linearGradient>
          <polyline
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_50_"
            fill="url(#XMLID_127_)"
            points="71.8,98.8 111.9,30.6 120.8,45.9 98.6,84.3 "
          />
          <linearGradient
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_128_"
            gradientUnits="userSpaceOnUse"
            x1="105.2901"
            y1="70.4664"
            x2="114.4151"
            y2="73.2164"
          >
            <stop offset="0" style={{ stopColor: '#B54E50' }} />
            <stop offset="1" style={{ stopColor: '#E04E50', stopOpacity: 0 }} />
          </linearGradient>
          <polyline
            xmlns="http://www.w3.org/2000/svg"
            id="XMLID_49_"
            fill="url(#XMLID_128_)"
            points="178.1,83.7 98.6,84.3 107.5,68.9 151.5,68.6 "
          />
        </>
      );
    case 'triangle':
      return (
        <>
          <polygon id="XMLID_129_" fill="#2459F5" points="49,0 88.7,68.9 71.5,68.9 49,30.6 9,98.8 0,83.2 " />
          <polygon
            id="XMLID_128_"
            fill="#E04E50"
            points="115.3,83.7 35.7,84.3 44.6,68.9 88.7,68.9 49,0 67.1,0 "
          />
          <polygon
            id="XMLID_127_"
            fill="#E7E3E8"
            points="9,98.8 49,30.6 58,45.9 35.7,84.3 115.3,83.7 108.8,98.8 "
          />
          <linearGradient
            id="XMLID_68_"
            gradientUnits="userSpaceOnUse"
            x1="62.1123"
            y1="113.9355"
            x2="62.1123"
            y2="90.9331"
          >
            <stop offset="0" style={{ stopColor: '#00002A', stopOpacity: 0.5 }} />
            <stop offset="0.5148" style={{ stopColor: '#79778D', stopOpacity: 0.2426 }} />
            <stop offset="1" style={{ stopColor: '#E7E3E8', stopOpacity: 0 }} />
          </linearGradient>
          <polygon
            id="XMLID_35_"
            fill="url(#XMLID_68_)"
            points="9,98.8 49,30.6 58,45.9 35.9,84.3 115.3,83.7 108.8,98.8 "
          />
          <linearGradient
            id="XMLID_69_"
            gradientUnits="userSpaceOnUse"
            x1="59.2933"
            y1="47.653"
            x2="55.058"
            y2="50.0982"
          >
            <stop offset="0" style={{ stopColor: '#001E2A', stopOpacity: 0.5 }} />
            <stop offset="1" style={{ stopColor: '#E7E3E8', stopOpacity: 0 }} />
          </linearGradient>
          <polygon id="XMLID_95_" fill="url(#XMLID_69_)" points="9,98.8 49,30.6 58,45.9 35.7,84.3 " />
          <linearGradient
            id="XMLID_70_"
            gradientUnits="userSpaceOnUse"
            x1="47.6636"
            y1="67.9163"
            x2="55.1174"
            y2="72.2198"
          >
            <stop offset="0" style={{ stopColor: '#B54E50', stopOpacity: 0.7 }} />
            <stop offset="1" style={{ stopColor: '#E04E50', stopOpacity: 0 }} />
          </linearGradient>
          <polygon id="XMLID_94_" fill="url(#XMLID_70_)" points="115.3,83.7 35.7,84.3 44.6,68.9 88.7,68.9 " />
        </>
      );
    case 'plain':
      return (
        <path
          xmlns="http://www.w3.org/2000/svg"
          d="M24.84 5.427V0H9.962v5.427h14.876zM15.414 44.82c4.51 0 8.482-1.742 11.107-4.823v4.153h7.472V10.988h-7.472v4.22c-2.625-3.081-6.597-4.89-11.107-4.89C6.26 10.318 0 18.022 0 27.603c0 9.514 6.26 17.218 15.415 17.218zm1.885-7.168c-5.655 0-9.693-4.221-9.693-10.05 0-5.829 4.038-10.117 9.693-10.117 5.318 0 9.222 4.288 9.222 10.117 0 5.896-3.971 10.05-9.222 10.05zM56.747 54c9.558 0 17.367-5.829 17.367-17.42V10.989h-7.136v4.957c-1.75-3.283-6.26-5.627-11.309-5.627-9.222 0-15.28 7.168-15.28 16.079 0 8.91 6.058 16.012 15.28 16.012 6.059 0 9.626-3.014 10.973-5.694v.268c0 6.766-3.5 11.322-10.636 11.322-3.433 0-7.337-1.139-9.963-3.417l-2.894 4.69C46.245 52.325 51.025 54 56.746 54zm.538-18.09c-5.25 0-9.29-4.02-9.29-9.513 0-5.494 4.04-9.58 9.29-9.58 5.25 0 9.357 4.02 9.357 9.312 0 5.762-3.972 9.782-9.357 9.782zm40.927 8.911c9.828 0 17.704-7.637 17.704-17.218 0-9.648-7.876-17.285-17.704-17.285S80.51 17.955 80.51 27.603c0 9.58 7.876 17.218 17.703 17.218zm0-7.168c-5.721 0-10.097-4.288-10.097-10.05 0-5.829 4.376-10.117 10.097-10.117 5.722 0 10.098 4.288 10.098 10.117 0 5.762-4.376 10.05-10.098 10.05zm31.571 6.498V30.618c0-8.71 4.712-12.194 12.049-12.194v-8.106c-7.135 0-10.77 3.886-12.049 7.235v-6.565h-7.472V44.15h7.472zm31.638.67c4.51 0 8.482-1.742 11.107-4.823v4.153H180V10.988h-7.472v4.22c-2.625-3.081-6.597-4.89-11.107-4.89-9.155 0-15.415 7.704-15.415 17.285 0 9.514 6.26 17.218 15.415 17.218zm1.885-7.168c-5.655 0-9.693-4.221-9.693-10.05 0-5.829 4.038-10.117 9.693-10.117 5.318 0 9.222 4.288 9.222 10.117 0 5.896-3.972 10.05-9.222 10.05z"
          fill={color}
        />
      );
  }
};
